import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: ""
  });

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setLoading(true);
    
    try {
      await register(formData.name, formData.email, formData.password);
      toast.success("Account created successfully!");
      navigate("/dashboard");
    } catch (error) {
      const message = error.response?.data?.detail || "Registration failed. Please try again.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    "AI-powered text summarization",
    "Customizable reading profiles",
    "Text-to-speech support",
    "Save and organize your texts"
  ];

  return (
    <div className="min-h-screen bg-[#F8F7FC] flex flex-col">
      {/* Header */}
      <header className="p-6">
        <Link to="/" className="inline-flex items-center gap-2 text-[#4A5568] hover:text-[#7C3AED] transition-colors">
          <ArrowLeft className="w-5 h-5" />
          <span className="font-medium">Back to Home</span>
        </Link>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-4xl grid md:grid-cols-2 gap-12 items-center">
          {/* Left Side - Benefits */}
          <div className="hidden md:block animate-slide-up">
            <h2 className="text-3xl font-bold text-[#1A202C] mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Start your journey to better reading
            </h2>
            <p className="text-[#4A5568] mb-8 text-lg">
              Join thousands of readers who have discovered a more accessible way to learn.
            </p>
            <ul className="space-y-4">
              {benefits.map((benefit, index) => (
                <li key={index} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#7C3AED] flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-[#4A5568]">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right Side - Form */}
          <div className="animate-slide-up">
            {/* Logo */}
            <div className="text-center mb-8 md:hidden">
              <Link to="/" className="inline-flex items-center gap-2 mb-4">
                <div className="flex items-center">
                  <span className="text-2xl font-bold text-[#1E3A8A]" style={{ fontFamily: 'Manrope, sans-serif' }}>READ</span>
                  <span className="text-2xl font-bold text-[#7C3AED]" style={{ fontFamily: 'Manrope, sans-serif' }}>BEST</span>
                </div>
              </Link>
            </div>

            {/* Register Card */}
            <Card className="border-none shadow-xl bg-white">
              <CardHeader className="space-y-1 pb-4">
                <div className="hidden md:flex items-center gap-1 justify-center mb-2">
                  <span className="text-xl font-bold text-[#1E3A8A]" style={{ fontFamily: 'Manrope, sans-serif' }}>READ</span>
                  <span className="text-xl font-bold text-[#7C3AED]" style={{ fontFamily: 'Manrope, sans-serif' }}>BEST</span>
                </div>
                <CardTitle className="text-2xl font-bold text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Create Account
                </CardTitle>
                <CardDescription className="text-center">
                  Sign up to start reading with ease
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="h-12"
                      data-testid="register-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="h-12"
                      data-testid="register-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      className="h-12"
                      data-testid="register-password-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      placeholder="••••••••"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      required
                      className="h-12"
                      data-testid="register-confirm-password-input"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full h-12 bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-semibold rounded-full"
                    disabled={loading}
                    data-testid="register-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating account...
                      </>
                    ) : (
                      "Create Account"
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Login Link */}
            <p className="text-center mt-6 text-[#4A5568]">
              Already have an account?{" "}
              <Link to="/login" className="text-[#7C3AED] font-semibold hover:underline" data-testid="login-link">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default RegisterPage;
